import Phaser from 'phaser';
import { ACHIEVEMENTS, visibleHow, type Achievement } from '../../config/achievements';
import { hubUnlocked } from '../../config/hub';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../../config/levels';
import { ProgressService } from '../../services/progressService';
import { RankService } from '../../services/rankService';
import { MAX_RANK, expToNext, tierForRank, type RankTier } from '../../services/rankMath';
import { saveStorage } from '../saveStorage';
import { getAudioService } from '../audio/AudioService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';
import { sweepAndToastAchievements } from '../../ui/achievementToast';

const GOLD_HEX = '#f5c518';
/** Blue exp-bar fill, matching the shop button family. */
const BAR_FILL = 0x2f7bd0;
const BAR_BG = 0x15131f;

/** Medal atlas key per rank tier. */
const MEDAL_KEY: Record<RankTier, string> = {
  bronze: 'medal-bronze',
  silver: 'medal-silver',
  gold: 'medal-gold',
  diamond: 'medal-diamond',
};

const TIER_LABEL: Record<RankTier, string> = {
  bronze: 'Bronze Tier',
  silver: 'Silver Tier',
  gold: 'Gold Tier',
  diamond: 'Diamond Tier',
};

/** Section heading colour per tier — Diamond reads ice-blue, the rest gold. */
const TIER_HEAD_HEX: Record<RankTier, string> = {
  bronze: GOLD_HEX,
  silver: GOLD_HEX,
  gold: GOLD_HEX,
  diamond: '#7fe3ff',
};

/** Rank bar geometry: medal circle at the left end of the track. */
const BAR_Y = 168;
const BAR_W = 560;
const BAR_H = 34;
const BAR_LEFT = 640 - BAR_W / 2 + 40; // shifted right so the medal overhangs
const MEDAL_SIZE = 110;

/**
 * Badge gallery geometry (spec Revision 6: a scrolling list, never shrunk).
 * Badges keep a fixed 64 px and a fixed 144 px pitch, eight to a row; a tier with more than eight wraps to another row, and the
 * whole gallery scrolls inside a viewport under the rank bar — wheel, drag
 * on the scrollbar's thumb, or the ▲/▼ at its ends.
 */
const BADGE_SIZE = 64;
const PITCH = 144;
const PER_ROW = 8;
/** Row block: label above, badge, two lines of name below. */
const ROW_H = 118;
const SECTION_HEAD_H = 34;
const SECTION_GAP = 10;
/** The scrolling viewport. */
const VIEW_TOP = 232;
const VIEW_BOTTOM = 706;
const VIEW_H = VIEW_BOTTOM - VIEW_TOP;
/** Scrollbar: a slim track at the right edge, thumb sized to the overflow. */
const BAR_X = 1246;
const TRACK_W = 10;
const THUMB_MIN = 40;
/** One wheel event scrolls its own distance, held between these (px). */
const WHEEL_MIN = 40;
const WHEEL_MAX = 240;
/** A press that travels further than this is a drag-scroll, not a badge click. */
const DRAG_SLOP = 8;

/**
 * AchievementsScene — the rank bar (medal + blue exp bar), the exp-to-next
 * line, and the badge gallery for every achievement in the registry, laid out
 * by tier in a scrolling list. Earned badges show full colour; unearned are
 * greyed. Clicking any badge opens a small panel with the name and how to get
 * it. Reached from the gold ACHIEVEMENTS button on the episode screen; BACK
 * returns there.
 */
export class AchievementsScene extends Phaser.Scene {
  private rankSvc!: RankService;
  private hubOpen = false;
  /**
   * The open badge-detail panel's objects (destroyed on close/reopen). A flat
   * list rather than a Container so `children.getByName` can find the named
   * pieces — the e2e driver reads them.
   */
  private detail: Phaser.GameObjects.GameObject[] = [];
  /** The scrolling gallery (every piece with its unscrolled y) and its scrollbar. */
  private gallery: { obj: Phaser.GameObjects.GameObject & { y: number }; baseY: number }[] = [];
  private contentH = 0;
  private scrollY = 0;
  private thumb?: Phaser.GameObjects.Rectangle;
  private thumbH = THUMB_MIN;
  /** A press on the gallery: where it started and the scroll at that moment. */
  private press: { y: number; scroll: number } | null = null;
  /** True once the current press has moved past DRAG_SLOP — badges ignore its release. */
  private dragged = false;

  constructor() {
    super({ key: 'AchievementsScene' });
  }

  create(): void {
    // Sweep FIRST, so anything already satisfied (a Diamond achievement done
    // before the update) draws earned rather than greyed.
    sweepAndToastAchievements(saveStorage());
    this.rankSvc = new RankService(saveStorage());
    const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
    this.hubOpen = hubUnlocked(progress);
    this.detail = [];
    this.scrollY = 0;
    this.press = null;
    this.dragged = false;

    this.add.image(640, 360, 'bg-shop').setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.6);

    this.add
      .text(640, 44, 'ACHIEVEMENTS', { fontSize: '38px', color: GOLD_HEX, fontStyle: 'bold' })
      .setOrigin(0.5);

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => this.scene.start('LevelSelectScene'));

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    this.renderRankBar();
    this.renderBadges();
    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  /** The medal + blue exp bar + "exp to rank up" line. */
  private renderRankBar(): void {
    const rank = this.rankSvc.rank();
    const exp = this.rankSvc.exp();
    const tier = tierForRank(rank);
    const need = expToNext(rank);
    const atMax = rank >= MAX_RANK;

    // Track (behind the medal so the bar appears to grow out of it).
    const track = this.add.graphics();
    track.fillStyle(BAR_BG, 0.9);
    track.fillRoundedRect(BAR_LEFT, BAR_Y - BAR_H / 2, BAR_W, BAR_H, 10);
    track.lineStyle(2, 0xffffff, 0.35);
    track.strokeRoundedRect(BAR_LEFT, BAR_Y - BAR_H / 2, BAR_W, BAR_H, 10);

    // Blue fill: exp / expToNext (full at max rank).
    const frac = atMax ? 1 : Math.max(0, Math.min(1, need > 0 ? exp / need : 0));
    if (frac > 0) {
      const fill = this.add.graphics();
      fill.fillStyle(BAR_FILL, 1);
      fill.fillRoundedRect(
        BAR_LEFT + 3,
        BAR_Y - BAR_H / 2 + 3,
        Math.max(12, (BAR_W - 6) * frac),
        BAR_H - 6,
        7,
      );
    }

    // Exp numbers centred on the bar, e.g. "150/750" (or MAX at rank 30).
    this.add
      .text(BAR_LEFT + BAR_W / 2, BAR_Y, atMax ? 'MAX' : `${exp}/${need}`, {
        fontSize: '18px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setShadow(0, 2, '#000000', 3)
      .setName('ach-exp-text');

    // Medal with the rank number on it, overhanging the bar's left end.
    const medalKey = MEDAL_KEY[tier];
    this.add.image(BAR_LEFT, BAR_Y, medalKey).setDisplaySize(MEDAL_SIZE, MEDAL_SIZE).setDepth(2);
    this.add
      .text(BAR_LEFT, BAR_Y, `${rank}`, {
        fontSize: '34px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(3)
      .setShadow(0, 2, '#000000', 4)
      .setName('ach-rank-number');

    // Progress line: how much more exp to rank up, and the tier.
    const line = atMax
      ? `Max rank reached — ${TIER_LABEL[tier]}`
      : `${need - exp} exp to rank up — ${TIER_LABEL[tier]}`;
    this.add
      .text(640, BAR_Y + 52, line, { fontSize: '18px', color: '#c8d2d8' })
      .setOrigin(0.5)
      .setName('ach-progress-line');
  }

  /**
   * The badge gallery: a section per tier with a small label, badges greyed
   * until earned, click for detail. Registry order within a tier is the
   * display order. Every piece is a top-level object (so `children.getByName`
   * finds a badge and its live position — the e2e driver clicks there); they
   * scroll together behind one shared geometry mask, and the scrollbar on the
   * right shows where you are.
   */
  private renderBadges(): void {
    const earned = new Set(this.rankSvc.earned());
    this.gallery = [];

    // The viewport mask: the gallery is clipped to [VIEW_TOP, VIEW_BOTTOM].
    const maskG = this.make.graphics();
    maskG.fillStyle(0xffffff);
    maskG.fillRect(0, VIEW_TOP, 1280, VIEW_H);
    const mask = maskG.createGeometryMask();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => maskG.destroy());
    const place = <
      T extends Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Mask & { y: number },
    >(
      o: T,
    ): T => {
      o.setMask(mask);
      this.gallery.push({ obj: o, baseY: o.y });
      return o;
    };

    let y = VIEW_TOP + 6;
    for (const tier of ['bronze', 'silver', 'gold', 'diamond'] as const) {
      const list = ACHIEVEMENTS.filter((a) => a.tier === tier);
      place(
        this.add
          .text(640, y + SECTION_HEAD_H / 2, TIER_LABEL[tier].toUpperCase(), {
            fontSize: '14px',
            color: TIER_HEAD_HEX[tier],
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setAlpha(0.85)
          .setName(`ach-tier-${tier}`),
      );
      y += SECTION_HEAD_H;

      const rows = Math.ceil(list.length / PER_ROW);
      for (let r = 0; r < rows; r++) {
        const row = list.slice(r * PER_ROW, (r + 1) * PER_ROW);
        const rowY = y + 44; // badge centre within the row block
        row.forEach((a, idx) => {
          // A full row spans GRID_SPAN centred on 640; a short last row is
          // centred too, so a tier's tail never hugs the left edge.
          const x = 640 - (PITCH * row.length) / 2 + PITCH / 2 + idx * PITCH;
          const has = earned.has(a.id);
          const img = place(
            this.add
              .image(x, rowY, a.icon)
              .setDisplaySize(BADGE_SIZE, BADGE_SIZE)
              .setName(`ach-badge-${a.id}`)
              .setInteractive({ useHandCursor: true }),
          );
          if (!has) img.setTint(0x555555).setAlpha(0.6);
          // Phaser hit-tests ignore the mask, so a badge scrolled out of the
          // viewport must not open its panel from under the rank bar.
          img.on('pointerup', () => {
            if (this.dragged) return; // the release of a drag-scroll
            if (img.y < VIEW_TOP || img.y > VIEW_BOTTOM) return;
            this.showDetail(a, has);
          });
          place(
            this.add
              .text(x, rowY + BADGE_SIZE / 2 + 4, a.name, {
                fontSize: '11px',
                color: has ? GOLD_HEX : '#8a929a',
                fontStyle: 'bold',
                align: 'center',
                wordWrap: { width: PITCH - 6 },
              })
              .setOrigin(0.5, 0),
          );
        });
        y += ROW_H;
      }
      y += SECTION_GAP;
    }
    this.contentH = y - VIEW_TOP;

    this.buildScrollbar();
    this.applyScroll();

    // Wheel anywhere scrolls the gallery by the wheel's own distance (a mouse
    // notch ~ one row, a trackpad smoothly). Line-mode wheels (Firefox) report
    // lines, not pixels.
    this.input.on('wheel', (p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => {
      if (this.detail.length > 0 || dy === 0) return;
      const ev = p.event as WheelEvent | undefined;
      const px = Math.abs(dy) * (ev?.deltaMode === 1 ? 40 : 1);
      this.scrollBy(Math.sign(dy) * Math.max(WHEEL_MIN, Math.min(WHEEL_MAX, px)));
    });

    // Click-and-drag (mouse or finger) anywhere on the gallery scrolls it, the
    // way a list on a phone does. The scrollbar column keeps its own drag.
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.dragged = false;
      const inGallery = p.y >= VIEW_TOP && p.y <= VIEW_BOTTOM && p.x < BAR_X - 16;
      this.press =
        inGallery && this.detail.length === 0 && this.maxScroll() > 0
          ? { y: p.y, scroll: this.scrollY }
          : null;
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.press || !p.isDown) return;
      const dy = p.y - this.press.y;
      if (!this.dragged && Math.abs(dy) < DRAG_SLOP) return;
      this.dragged = true;
      this.scrollY = this.press.scroll - dy;
      this.applyScroll();
    });
    // Scene-level pointerup fires AFTER a badge's own pointerup, so the badge
    // still sees `dragged` for this release; clear it for the next press.
    this.input.on('pointerup', () => {
      this.press = null;
      this.time.delayedCall(0, () => (this.dragged = false));
    });
  }

  private maxScroll(): number {
    return Math.max(0, this.contentH - VIEW_H);
  }

  /** The scrollbar: track, draggable thumb, and ▲/▼ at the ends. */
  private buildScrollbar(): void {
    const max = this.maxScroll();
    this.add
      .rectangle(BAR_X, VIEW_TOP + VIEW_H / 2, TRACK_W, VIEW_H, 0x15131f, 0.9)
      .setStrokeStyle(1, 0xffffff, 0.25)
      .setName('ach-scroll-track');
    // Thumb length is the visible fraction of the content (never tiny).
    this.thumbH =
      max === 0 ? VIEW_H : Math.max(THUMB_MIN, Math.round((VIEW_H * VIEW_H) / this.contentH));
    const thumb = this.add
      .rectangle(
        BAR_X,
        VIEW_TOP + this.thumbH / 2,
        TRACK_W,
        this.thumbH,
        0xf5c518,
        max === 0 ? 0.35 : 0.9,
      )
      .setName('ach-scroll-thumb')
      .setInteractive({ useHandCursor: max > 0, draggable: max > 0 });
    this.thumb = thumb;
    if (max > 0) {
      thumb.on('drag', (_p: Phaser.Input.Pointer, _x: number, dragY: number) => {
        const travel = VIEW_H - this.thumbH;
        const top = Math.max(
          VIEW_TOP,
          Math.min(VIEW_BOTTOM - this.thumbH, dragY - this.thumbH / 2),
        );
        this.scrollY = travel > 0 ? ((top - VIEW_TOP) / travel) * this.maxScroll() : 0;
        this.applyScroll();
      });
    }
    const arrow = (y: number, glyph: string, dir: -1 | 1, name: string) => {
      const t = this.add
        .text(BAR_X, y, glyph, { fontSize: '16px', color: max > 0 ? GOLD_HEX : '#555a66' })
        .setOrigin(0.5)
        .setName(name);
      if (max > 0) {
        t.setInteractive({ useHandCursor: true });
        t.on('pointerup', () => this.scrollBy(dir * ROW_H));
      }
    };
    arrow(VIEW_TOP - 14, '▲', -1, 'ach-scroll-up');
    arrow(VIEW_BOTTOM + 8, '▼', 1, 'ach-scroll-down');
  }

  /** Scroll by `dy` px (clamped). Public so a driver can page the gallery. */
  scrollBy(dy: number): void {
    this.scrollY = Math.max(0, Math.min(this.maxScroll(), this.scrollY + dy));
    this.applyScroll();
  }

  private applyScroll(): void {
    const max = this.maxScroll();
    this.scrollY = Math.max(0, Math.min(max, this.scrollY));
    for (const { obj, baseY } of this.gallery) obj.y = baseY - this.scrollY;
    if (this.thumb) {
      const travel = VIEW_H - this.thumbH;
      const top = VIEW_TOP + (max > 0 ? (this.scrollY / max) * travel : 0);
      this.thumb.setY(top + this.thumbH / 2);
    }
  }

  /** Small centre panel: the achievement's name and how you get/got it. */
  private showDetail(a: Achievement, has: boolean): void {
    this.closeDetail();
    const D = 20;
    const keep = (o: Phaser.GameObjects.GameObject) => {
      this.detail.push(o);
      return o;
    };

    // Scrim closes on click and swallows the pointer from the grid below.
    keep(
      this.add
        .rectangle(640, 360, 1280, 720, 0x000000, 0.55)
        .setDepth(D)
        .setInteractive()
        .on('pointerup', () => this.closeDetail()),
    );

    const g = this.add.graphics().setDepth(D + 1);
    g.fillStyle(0x1a1726, 0.97);
    g.fillRoundedRect(640 - 260, 360 - 130, 520, 260, 14);
    g.lineStyle(3, 0xcd7f32, 1);
    g.strokeRoundedRect(640 - 260, 360 - 130, 520, 260, 14);
    keep(g);

    const icon = this.add
      .image(640 - 180, 360 - 30, a.icon)
      .setDisplaySize(96, 96)
      .setDepth(D + 2);
    if (!has) icon.setTint(0x555555).setAlpha(0.7);
    keep(icon);

    keep(
      this.add
        .text(640 + 30, 360 - 60, a.name, {
          fontSize: '26px',
          color: GOLD_HEX,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2)
        .setName('ach-detail-name'),
    );
    keep(
      this.add
        // Mystery rule: unearned silver/gold read "???" until earned; so does
        // the one hidden bronze (Scientist) until The Hub is open.
        .text(640 + 30, 360 - 10, visibleHow(a, has, this.hubOpen), {
          fontSize: '17px',
          color: '#e8ecef',
          wordWrap: { width: 320 },
          align: 'center',
        })
        .setOrigin(0.5, 0)
        .setDepth(D + 2)
        .setName('ach-detail-how'),
    );
    if (a.limited === 'origin') {
      keep(
        this.add
          .text(640 + 30, 360 + 40, 'LIMITED TIME — Origin Event', {
            fontSize: '13px',
            color: '#ff9b5b',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(D + 2)
          .setName('ach-detail-limited'),
      );
    }
    keep(
      this.add
        .text(640, 360 + 66, has ? 'EARNED' : 'NOT EARNED YET', {
          fontSize: '15px',
          color: has ? '#4caf50' : '#8a929a',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );

    const close = this.add
      .text(640, 360 + 102, 'CLOSE', {
        fontSize: '17px',
        color: '#1a1a1a',
        fontStyle: 'bold',
        backgroundColor: '#f5c518',
        padding: { x: 20, y: 8 },
      })
      .setOrigin(0.5)
      .setDepth(D + 2)
      .setName('ach-detail-close')
      .setInteractive({ useHandCursor: true });
    close.on('pointerup', () => this.closeDetail());
    keep(close);
  }

  private closeDetail(): void {
    for (const o of this.detail) o.destroy();
    this.detail = [];
  }
}
